import { useMemo } from "react";
import {
  ArrayPath,
  Control,
  FieldArray,
  FieldErrorsImpl,
  UseFormRegister,
  useFieldArray,
} from "react-hook-form";
import { WordsInputListContent } from "content";
import { Space } from "components/Common/Space";
import { InputLabel } from "components/Common/InputLabel";
import { Button } from "components/Interactive/Button";
import { InputList } from "./InputList";
import { getWordsInputInitialValue } from "./WordsInputList.helpers";
import { TWordsInputDefaultValue } from "./WordsInputList.types";
import "./WordsInputList.styles.scss";

type TWordsInputListProps<
  TFormValues extends TWordsInputDefaultValue = TWordsInputDefaultValue
> = {
  /**
   * Name attribute of the `WordsInputList` component.
   * Also this field is required for `react-hook-form` to control element.
   */
  name: ArrayPath<TFormValues>;
  /**
   * Label text for the `WordsInputList` component.
   */
  label?: string;
  /**
   * The short hint displayed in the word `input` before the user enters a value.
   */
  wordPlaceholder?: string;
  /**
   * The short hint displayed in the hint `input` before the user enters a value.
   */
  hintPlaceholder?: string;
  /**
   * The content of the add input button.
   */
  addInputButtonText?: string;
  /**
   * The text hint displayed as tooltip popup right to the label, which looks like a question icon button. Hint will appear when user clicks on the hint button.
   */
  hint?: string;
  /**
   * This object contains methods for registering components into React Hook Form.
   */
  control?: Control<TFormValues>;
  /**
   * This method allows you to register an input or select element and apply validation rules to React Hook Form. Validation rules are all based on the HTML standard and also allow for custom validation methods.
   * @link https://react-hook-form.com/api/useform/register
   */
  register?: UseFormRegister<TFormValues>;
  /**
   * An object with field errors.
   */
  errors?: Partial<FieldErrorsImpl<TFormValues>>;
  /**
   * Override or extend the styles applied to the component.
   */
  className?: string;
};

/**
 * `WordsInputList` interactive element in association with `react-hook-form` library made for `ExerciseConstructor` component.
 */
export const WordsInputList = <
  TFormValues extends TWordsInputDefaultValue = TWordsInputDefaultValue
>(
  props: TWordsInputListProps<TFormValues>
): JSX.Element => {
  const {
    label = WordsInputListContent.Default.LABEL,
    wordPlaceholder = WordsInputListContent.Default.WORD_PLACEHOLDER,
    hintPlaceholder = WordsInputListContent.Default.HINT_PLACEHOLDER,
    addInputButtonText = WordsInputListContent.Default.ADD_INPUT_BUTTON,
    hint,
    name,
    control,
    register,
    errors,
    className,
  } = props;

  const { fields, remove, append } = useFieldArray<TFormValues>({
    control,
    name,
  });

  const addInputClickHandler = () => {
    append(
      getWordsInputInitialValue() as FieldArray<
        TFormValues,
        ArrayPath<TFormValues>
      >
    );
  };

  const removeInputClickHandler = (inputIndex: number) => {
    remove(inputIndex);
  };

  const firstFieldId: string = useMemo(() => {
    const [firstField] = fields;
    return firstField?.id || "";
  }, [fields]);

  return (
    <Space
      direction="vertical"
      justify="start"
      size="x-small"
      fullWidth
      className={className}
    >
      <InputLabel
        label={label}
        hint={hint}
        id={`words-input-list-content-${firstFieldId}`}
      />
      <InputList<TFormValues>
        name={name}
        wordPlaceholder={wordPlaceholder}
        hintPlaceholder={hintPlaceholder}
        fields={fields}
        register={register}
        errors={errors}
        onDelete={removeInputClickHandler}
      />
      <Button variant="secondary" fullWidth onClick={addInputClickHandler}>
        {addInputButtonText}
      </Button>
    </Space>
  );
};
